import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileDown, Search } from 'lucide-react';
import { listEmpresas } from '@lib/services/empresas.service';
import { CampoFecha } from './CampoFecha';

/**
 * Piezas comunes de los reportes (Reporte ABM Web, Reporte de padrón): el form
 * de filtros, el rango de fechas, la organización, el botón de buscar y la
 * barra con el total y la descarga. Cada reporte arma sus filtros propios con
 * esto, así las dos pantallas se ven y se comportan igual.
 */

/** El form de filtros: fila que se parte en varias líneas si no entra. */
export function FormFiltros({ onSubmit, children }: { onSubmit: () => void; children: ReactNode }) {
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit();
  }
  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        marginBottom: 18,
      }}
    >
      {children}
    </form>
  );
}

/**
 * "Desde" y "hasta" como `<input type="date">`, que siempre manda aaaa-mm-dd
 * (el formato que valida el server). Cada uno acota al otro para no poder armar
 * un rango al revés.
 */
export function RangoFechas({
  prefijo = 'Fecha',
  desde,
  hasta,
  onDesde,
  onHasta,
}: {
  prefijo?: string;
  desde: string;
  hasta: string;
  onDesde: (v: string) => void;
  onHasta: (v: string) => void;
}) {
  return (
    <>
      {/* `CampoFecha` y no `<input type="date">`: el nativo se dibuja en el
          formato del navegador (mm/dd/aaaa en una máquina en inglés). */}
      <div className="formField" style={{ maxWidth: 210 }}>
        <span>{prefijo} desde</span>
        <CampoFecha ariaLabel={`${prefijo} desde`} value={desde} onChange={onDesde} />
      </div>

      <div className="formField" style={{ maxWidth: 210 }}>
        <span>{prefijo} hasta</span>
        <CampoFecha ariaLabel={`${prefijo} hasta`} value={hasta} onChange={onHasta} />
      </div>
    </>
  );
}

/** Organización a filtrar. Para la organización el server la pisa con la suya. */
export function SelectOrganizacion({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const empresas = useQuery({ queryKey: ['empresas', ''], queryFn: () => listEmpresas() });
  return (
    <label className="formField" style={{ maxWidth: 220 }}>
      <span>Organización</span>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— Todas —</option>
        {(empresas.data ?? []).map((emp) => (
          <option key={emp.id} value={emp.id}>
            {emp.nombreComercial}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Campo de texto libre angosto (legajo, DNI). */
export function CampoTexto({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="formField" style={{ maxWidth: 140 }}>
      <span>{label}</span>
      <input
        type="text"
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function BotonBuscar({ loading }: { loading?: boolean }) {
  return (
    <button
      type="submit"
      className="btn btn-primary"
      disabled={loading}
      style={{ alignSelf: 'flex-end' }}
    >
      <Search size={14} />
      {loading ? 'Buscando…' : 'Buscar'}
    </button>
  );
}

/**
 * Total del filtro y el botón de descarga. La descarga baja TODO el filtro, no
 * la página que se ve, por eso va por el export async y no arma el archivo acá.
 */
export function BarraResultados({
  total,
  onDescargar,
}: {
  total: number;
  onDescargar: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
      }}
    >
      <p className="text-element-mid" style={{ fontSize: 13 }}>
        {total} {total === 1 ? 'resultado' : 'resultados'}
      </p>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={onDescargar}
        disabled={total === 0}
        title="Descargar todos los resultados del filtro"
      >
        <FileDown size={14} />
        Descargar Excel
      </button>
    </div>
  );
}
